import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Button, InputNumber, Slider, Tooltip } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import styles from './styles/salary-input.module.css';
import sharedStyles from './styles/shared-components.module.css';
import { addAnswer } from '@/store/slices/chatbotFlow';
import { findClosest, roundSalary, formatCurrency } from '@/helpers/SalaryInputHelper';
import { useTranslation } from 'react-i18next';

const SalaryInput = ({ block, addAnswer,isChatbotVisible,animTransition }) => {
  const { t } = useTranslation();
  const [input, setInput] = useState([0, 0]);
  const [numberInput, setNumberInput] = useState(null);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(0);
  const [step, setStep] = useState(1);
  const [period, setPeriod] = useState('annually');

  useEffect(() => {
    initializeSalaryRange();
  }, [block]);

  const initializeSalaryRange = () => {
    const DEFAULT_MIN = 15000;
    const DEFAULT_MAX = 250000;

    const min = block?.options?.min ?? DEFAULT_MIN;
    const max = block?.options?.max ?? DEFAULT_MAX;
    const range = max - min;
    const onePercentStep = Math.round(range / 100);
    const step = findClosest(onePercentStep, [1, 10, 50, 100, 500, 1000, 5000]);

    setInput([min, max]);
    setMin(min);
    setMax(max);
    setStep(step);
  };

  const submitRangeInput = () => {
    addAnswer({ min: input[0], max: input[1] });
  };

  const onSliderChange = (value) => {
    setInput(value);
  };

  const submitNumberInput = () => {
    const trimmedInput = numberInput ? numberInput.toString().trim() : '';
    const parsedNumber = parseInt(trimmedInput, 10);

    if (!trimmedInput || isNaN(parsedNumber) || trimmedInput.startsWith('0')) {
      setShowError(true);
      setErrorMessage(t('invalidNumber'));
      setNumberInput(null);
      return;
    }

    addAnswer({ min: parsedNumber, max: null });
    setShowError(false);
    setErrorMessage('');
  };

  const handleNumberChange = (value) => {
    setShowError(false);
    setErrorMessage('');
    setNumberInput(value);
  };

  const handlePeriodChange = (value) => {
    setPeriod(value);
  };

  const getPlaceholder = () => {
    const { currency = 'GBP', period } = block?.options;
    switch (period) {
      case 'annually':
        return t('enterAnnualSalary', { currency });
      case 'monthly':
        return t('enterMonthlySalary', { currency });
      case 'daily':
        return t('enterDailyWage', { currency });
      case 'hourly':
        return t('enterHourlyRate', { currency });
      default:
        return t('enterAmount', { currency });
    }
  };

  const renderNumberInput = () => {
    const { currency = 'GBP' } = block?.options;

    return (
      <div className={sharedStyles.wrapper}>
        <div className={styles.input_container}>
          <InputNumber
            style={{ width: '100%' }}
            className={styles.input}
            value={numberInput}
            autoFocus
            placeholder={getPlaceholder()}
            prefix={<div style={{ color: "#969696" }}>{formatCurrency(null, currency)}</div>}
            variant={'borderless'}
            controls={false}
            onChange={handleNumberChange}
            onPressEnter={submitNumberInput}
          />
        </div>
        <Tooltip
          placement="topRight"
          align={{ offset: [-5, -5] }}
          title={errorMessage}
          open={showError && isChatbotVisible && animTransition}
        >
          <Button
            className={sharedStyles.send_button}
            type="text"
            danger={showError}
            onClick={submitNumberInput}
            icon={<SendOutlined style={{ fontSize: '24px' }} />}
          />
        </Tooltip>
      </div>
    );
  };

  const renderSliderInput = () => {
    const { currency = 'GBP', period } = block?.options;
    const translatedPeriod = t(`periods.${period}`);

    return (
      <div className={sharedStyles.wrapper}>
        <div className={`${styles.input_container_slider} ${styles.input_container}`}>
          <Slider
            range
            step={step}
            min={min}
            max={max}
            tooltip={{ placement: 'top', formatter: (num) => formatCurrency(num, currency) }}
            onChange={onSliderChange}
            value={input}
          />
        </div>
        <Button
          className={sharedStyles.send_button}
          type="text"
          danger={showError}
          onClick={submitRangeInput}
          icon={<SendOutlined style={{ fontSize: '24px' }} />}
        />
      </div>
    );
  };

  const inputType = block?.options?.inputType;

  return inputType === 'number' ? renderNumberInput() : renderSliderInput();
};

SalaryInput.propTypes = {
  block: PropTypes.object.isRequired,
  addAnswer: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  isChatbotVisible: state.chatbotVisibility.isVisible,
  animTransition: state.chatbotVisibility.animTransition,
});


const mapDispatchToProps = { addAnswer };
export default connect(mapStateToProps, mapDispatchToProps)(SalaryInput);